import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useState } from "react"

import { type CalendarFilterParticipant } from "../../../redux/calendar.types.ts"
import { useMiniMonthEventDays } from "./useMiniMonthEventDays.ts"

const { Text } = Typography

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
const DAY_KEY_FORMAT = "YYYY-MM-DD"

interface CalendarMiniMonthProps {
  currentDate: Date
  timezone: string
  // The Calendar's own Team filter — the mini-month reflects the same scope
  // the user is currently viewing, not a separate one: a filtered team
  // member's dots, or everything this account permits when nothing is
  // filtered. See useMiniMonthEventDays for how that's resolved.
  filterParticipants?: CalendarFilterParticipant[] | null
  // Bumps the mini-month's own cached dots-fetch (see useMiniMonthEventDays)
  // whenever the caller's own event data has actually changed — the fetch
  // has no other way to learn that an event was created/edited/deleted
  // elsewhere on the page.
  refreshSignal?: string
  onSelectDate: (date: Date) => void
}

export function CalendarMiniMonth({
  currentDate,
  timezone,
  filterParticipants = null,
  refreshSignal,
  onSelectDate
}: CalendarMiniMonthProps) {
  const selected = useMemo(() => dayjs(currentDate).tz(timezone), [
    currentDate,
    timezone
  ])
  const [displayedMonth, setDisplayedMonth] = useState(() =>
    selected.startOf("month")
  )

  // Follows the main calendar's own navigation (Prev/Next/Today, a header
  // click, a Lead deep-link) — but only when that outer date actually moves
  // to a different month, so browsing the mini-month itself with its own
  // prev/next isn't fought by this effect on every render.
  const selectedMonthKey = selected.format("YYYY-MM")
  useEffect(() => {
    setDisplayedMonth(dayjs.tz(selectedMonthKey, "YYYY-MM", timezone))
  }, [selectedMonthKey, timezone])

  const today = useMemo(() => dayjs().tz(timezone), [timezone])

  // The mini-month's own reliable answer to "which days this month have
  // events" — independent of whatever date range the main calendar's
  // current view happens to have fetched (see useMiniMonthEventDays).
  const { eventDays } = useMiniMonthEventDays({
    displayedMonth,
    timezone,
    filterParticipants,
    refreshSignal
  })

  // A 6-row grid starting on the Sunday on/before the 1st, so short and long
  // months alike always show a full, unbroken set of weeks.
  const weeks = useMemo(() => {
    const start = displayedMonth.startOf("week")
    const cells: Dayjs[] = []
    for (let i = 0; i < 42; i += 1) cells.push(start.add(i, "day"))

    const rows: Dayjs[][] = []
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7))
    return rows
  }, [displayedMonth])

  return (
    <div className={"rounded-xl border border-gray-200 p-3"}>
      <Flex align={"center"} justify={"space-between"} className={"mb-2"}>
        <Text strong className={"text-sm"}>
          {displayedMonth.format("MMMM YYYY")}
        </Text>
        <Flex gap={0}>
          <Button
            type={"text"}
            size={"small"}
            aria-label={"Previous month"}
            icon={<LeftOutlined className={"text-xs"} />}
            // Browsing the mini-month's own month is independent of the
            // main calendar's date — this only ever updates local state,
            // never calls onSelectDate, so it can't move the main view.
            onClick={() => setDisplayedMonth(displayedMonth.subtract(1, "month"))}
          />
          <Button
            type={"text"}
            size={"small"}
            aria-label={"Next month"}
            icon={<RightOutlined className={"text-xs"} />}
            onClick={() => setDisplayedMonth(displayedMonth.add(1, "month"))}
          />
        </Flex>
      </Flex>

      <div className={"grid grid-cols-7 gap-y-1 text-center"}>
        {WEEKDAY_LABELS.map((label) => (
          <Text key={label} type={"secondary"} className={"text-[10px] font-medium"}>
            {label}
          </Text>
        ))}

        {weeks.flat().map((day) => {
          const inMonth = day.isSame(displayedMonth, "month")
          const isToday = day.isSame(today, "day")
          const isSelected = day.isSame(selected, "day")
          const hasEvents = eventDays.has(day.format(DAY_KEY_FORMAT))

          return (
            <button
              key={day.format(DAY_KEY_FORMAT)}
              type={"button"}
              aria-label={day.format("MMMM D, YYYY")}
              // Selecting a date always navigates immediately — it only ever
              // calls onSelectDate with the plain clicked date, never waits
              // on the event-dots fetch above (a separate, decoupled
              // concern), so this stays instantaneous regardless of network
              // latency.
              onClick={() => onSelectDate(day.toDate())}
              className={[
                "relative mx-auto flex size-7 items-center justify-center rounded-full text-xs transition-colors",
                !inMonth ? "text-gray-300" : "text-gray-700",
                isSelected
                  ? "bg-teal-600 text-white font-semibold"
                  : isToday
                    ? "border border-teal-600 font-semibold text-teal-700"
                    : "hover:bg-gray-100"
              ].join(" ")}
            >
              {day.format("D")}
              {hasEvents && !isSelected && (
                <span
                  className={
                    "absolute bottom-0.5 size-1 rounded-full bg-teal-500"
                  }
                />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
